import { concepts as PROBLEM_CATEGORY_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/code-systems/problem-category.js';
import { concepts as PROGRAM_TYPES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/code-systems/program-types.js';
import { concepts as ALOPEX_ETHNICITIES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/alopex-ethnicities.js';
import { concepts as ALOPEX_RACES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/alopex-races.js';
import { concepts as ALOPEX_RELIGIONS_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/alopex-religions.js';
import { concepts as COMMON_LANGUAGES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/common-languages.js';
import { concepts as GENDERS_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/genders.js';
import { concepts as MARITAL_STATUSES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/marital-statuses.js';
import { concepts as MEDICATION_FREQUENCIES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/medication-frequency.js';
import { concepts as MEDICATION_ROUTES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/medication-route.js';
import { concepts as SUPPORTED_TIMEZONES_CONCEPTS } from '@sf2/fhir-types/generated/terminologies/value-sets/supported-timezones.js';

import { CUSTOM_CODE_SYSTEMS } from './fhir-systems';

export type ReferenceDataItem = {
  code: string;
  display: string;
  system?: string;
};

const fromConcepts = (
  concepts: readonly { code: string; displayUi: string; system: string }[],
): ReferenceDataItem[] =>
  concepts.map((c) => ({ code: c.code, display: c.displayUi, system: c.system }));

export const GENDERS: ReferenceDataItem[] = fromConcepts(GENDERS_CONCEPTS);

export const MARITAL_STATUSES: ReferenceDataItem[] = fromConcepts(MARITAL_STATUSES_CONCEPTS);

export const LANGUAGES: ReferenceDataItem[] = fromConcepts(COMMON_LANGUAGES_CONCEPTS);

export const ETHNICITIES: ReferenceDataItem[] = fromConcepts(ALOPEX_ETHNICITIES_CONCEPTS);

export const RACES: ReferenceDataItem[] = fromConcepts(ALOPEX_RACES_CONCEPTS);

export const RELIGIONS: ReferenceDataItem[] = fromConcepts(ALOPEX_RELIGIONS_CONCEPTS);

export const PROGRAMS: ReferenceDataItem[] = fromConcepts(PROGRAM_TYPES_CONCEPTS);

export const PROBLEM_CATEGORIES: ReferenceDataItem[] = fromConcepts(PROBLEM_CATEGORY_CONCEPTS);

const PROBLEM_CATEGORY_LABELS = new Map(PROBLEM_CATEGORIES.map((c) => [c.code, c.display]));

export const problemCategoryLabel = (code: string | undefined): string | undefined =>
  code ? (PROBLEM_CATEGORY_LABELS.get(code) ?? code) : undefined;

export const MEDICATION_FREQUENCIES: ReferenceDataItem[] = fromConcepts(
  MEDICATION_FREQUENCIES_CONCEPTS,
);

export const MEDICATION_ROUTES: ReferenceDataItem[] = fromConcepts(MEDICATION_ROUTES_CONCEPTS);

export const SUPPORTED_TIMEZONES: ReferenceDataItem[] = fromConcepts(SUPPORTED_TIMEZONES_CONCEPTS);

export const ENTITY_TYPES: ReferenceDataItem[] = [
  { code: 'organization', display: 'Organization', system: CUSTOM_CODE_SYSTEMS.ENTITY_TYPES },
  { code: 'practice', display: 'Practice', system: CUSTOM_CODE_SYSTEMS.ENTITY_TYPES },
  { code: 'clinic', display: 'Clinic', system: CUSTOM_CODE_SYSTEMS.ENTITY_TYPES },
  { code: 'hospital', display: 'Hospital', system: CUSTOM_CODE_SYSTEMS.ENTITY_TYPES },
  { code: 'health-system', display: 'Health System', system: CUSTOM_CODE_SYSTEMS.ENTITY_TYPES },
  { code: 'medical-group', display: 'Medical Group', system: CUSTOM_CODE_SYSTEMS.ENTITY_TYPES },
];

export type VitalTypeItem = ReferenceDataItem & {
  defaultUnits: string;
  loincCode: string;
  hasSecondaryValue?: boolean;
};

export const VITAL_TYPES: VitalTypeItem[] = [
  {
    code: '85354-9',
    display: 'Blood Pressure',
    system: 'http://loinc.org',
    defaultUnits: 'mm[Hg]',
    loincCode: '85354-9',
    hasSecondaryValue: true,
  },
  {
    code: '8867-4',
    display: 'Heart Rate',
    system: 'http://loinc.org',
    defaultUnits: '/min',
    loincCode: '8867-4',
  },
  {
    code: '8310-5',
    display: 'Body Temperature',
    system: 'http://loinc.org',
    defaultUnits: '[degF]',
    loincCode: '8310-5',
  },
  {
    code: '2339-0',
    display: 'Blood Glucose',
    system: 'http://loinc.org',
    defaultUnits: 'mg/dL',
    loincCode: '2339-0',
  },
  {
    code: '9279-1',
    display: 'Respiratory Rate',
    system: 'http://loinc.org',
    defaultUnits: '/min',
    loincCode: '9279-1',
  },
  {
    code: '2708-6',
    display: 'Oxygen Saturation',
    system: 'http://loinc.org',
    defaultUnits: '%',
    loincCode: '2708-6',
  },
  {
    code: '29463-7',
    display: 'Body Weight',
    system: 'http://loinc.org',
    defaultUnits: 'kg',
    loincCode: '29463-7',
  },
  {
    code: '8302-2',
    display: 'Body Height',
    system: 'http://loinc.org',
    defaultUnits: 'cm',
    loincCode: '8302-2',
  },
];

export const VITAL_POSITIONS: ReferenceDataItem[] = [
  { code: 'sitting', display: 'Sitting', system: 'http://snomed.info/sct' },
  { code: 'standing', display: 'Standing', system: 'http://snomed.info/sct' },
  { code: 'lying', display: 'Lying Down', system: 'http://snomed.info/sct' },
  { code: 'reclining', display: 'Reclining', system: 'http://snomed.info/sct' },
];

export const VITAL_METHODS: ReferenceDataItem[] = [
  { code: 'manual', display: 'Manual', system: CUSTOM_CODE_SYSTEMS.VITAL_METHODS },
  { code: 'automatic', display: 'Automatic/Device', system: CUSTOM_CODE_SYSTEMS.VITAL_METHODS },
  {
    code: 'patient-reported',
    display: 'Patient Reported',
    system: CUSTOM_CODE_SYSTEMS.VITAL_METHODS,
  },
];

export const CONSENT_TYPES: ReferenceDataItem[] = [
  { code: 'verbal', display: 'Verbal Consent', system: CUSTOM_CODE_SYSTEMS.CONSENT_TYPES },
  {
    code: 'written',
    display: 'Written Consent (Upload)',
    system: CUSTOM_CODE_SYSTEMS.CONSENT_TYPES,
  },
  {
    code: 'electronic',
    display: 'Electronic Consent',
    system: CUSTOM_CODE_SYSTEMS.CONSENT_TYPES,
  },
];

export const CONSENT_PARTIES: ReferenceDataItem[] = [
  { code: 'patient', display: 'Patient', system: CUSTOM_CODE_SYSTEMS.CONSENT_PARTIES },
  {
    code: 'proxy',
    display: 'Authorized Representative/Proxy',
    system: CUSTOM_CODE_SYSTEMS.CONSENT_PARTIES,
  },
];

export const CODE_STATUSES: ReferenceDataItem[] = [
  { code: 'active', display: 'Active', system: CUSTOM_CODE_SYSTEMS.CONDITION_STATUS },
  { code: 'resolved', display: 'Resolved', system: CUSTOM_CODE_SYSTEMS.CONDITION_STATUS },
  { code: 'inactive', display: 'Inactive', system: CUSTOM_CODE_SYSTEMS.CONDITION_STATUS },
  { code: 'recurrence', display: 'Recurrence', system: CUSTOM_CODE_SYSTEMS.CONDITION_STATUS },
];

export type TrackingCategoryItem = ReferenceDataItem & {
  description?: string;
};

export const TRACKING_CATEGORIES: TrackingCategoryItem[] = [
  {
    code: 'clinical-care',
    display: 'Clinical Care',
    description: 'Time spent on clinical activities',
    system: CUSTOM_CODE_SYSTEMS.TRACKING_CATEGORIES,
  },
  {
    code: 'administrative',
    display: 'Administrative',
    description: 'Time spent on administrative tasks',
    system: CUSTOM_CODE_SYSTEMS.TRACKING_CATEGORIES,
  },
  {
    code: 'documentation',
    display: 'Documentation',
    description: 'Time spent on documentation',
    system: CUSTOM_CODE_SYSTEMS.TRACKING_CATEGORIES,
  },
];

export type TrackingSubcategoryItem = ReferenceDataItem & {
  categoryCode: string;
  description?: string;
  uniqueName?: string;
};

export const TRACKING_SUBCATEGORIES: TrackingSubcategoryItem[] = [
  {
    code: 'patient-assessment',
    display: 'Patient Assessment',
    description: 'Initial patient assessment and evaluation',
    categoryCode: 'clinical-care',
    uniqueName: 'PATIENT_ASSESSMENT',
    system: CUSTOM_CODE_SYSTEMS.TRACKING_SUBCATEGORIES,
  },
  {
    code: 'care-coordination',
    display: 'Care Coordination',
    description: 'Coordinating care with other providers',
    categoryCode: 'clinical-care',
    uniqueName: 'CARE_COORDINATION',
    system: CUSTOM_CODE_SYSTEMS.TRACKING_SUBCATEGORIES,
  },
  {
    code: 'scheduling',
    display: 'Scheduling',
    description: 'Scheduling appointments and tasks',
    categoryCode: 'administrative',
    uniqueName: 'SCHEDULING',
    system: CUSTOM_CODE_SYSTEMS.TRACKING_SUBCATEGORIES,
  },
  {
    code: 'chart-review',
    display: 'Chart Review',
    description: 'Reviewing patient charts and documentation',
    categoryCode: 'documentation',
    uniqueName: 'CHART_REVIEW',
    system: CUSTOM_CODE_SYSTEMS.TRACKING_SUBCATEGORIES,
  },
];

export const RELATIONSHIPS: ReferenceDataItem[] = [
  { code: 'spouse', display: 'Spouse', system: CUSTOM_CODE_SYSTEMS.RELATIONSHIPS },
  { code: 'parent', display: 'Parent', system: CUSTOM_CODE_SYSTEMS.RELATIONSHIPS },
  { code: 'child', display: 'Child', system: CUSTOM_CODE_SYSTEMS.RELATIONSHIPS },
  { code: 'sibling', display: 'Sibling', system: CUSTOM_CODE_SYSTEMS.RELATIONSHIPS },
  { code: 'friend', display: 'Friend', system: CUSTOM_CODE_SYSTEMS.RELATIONSHIPS },
  { code: 'other', display: 'Other', system: CUSTOM_CODE_SYSTEMS.RELATIONSHIPS },
];
